/**
 * Authentication service using Supabase anonymous auth
 *
 * Manages user identity with stable UUIDs that persist across sessions.
 * Handles migration from localStorage-based identity to Supabase profiles.
 */

import * as Sentry from '@sentry/nextjs'
import { supabase } from './supabase'
import { isDesktopApp } from './desktopApp'
import { sendSignupNotification } from '@/app/[locale]/(marketing)/actions/signup'
import {
  getStoredReferralCode,
  getStoredReferralContext,
  clearStoredReferralCode,
  getStoredPromoSlug,
  clearStoredPromoSlug,
  isSentinelRefCode,
  type ReferralSource,
} from '@/lib/signupAccessStorage'

// =============================================================================
// STALE SESSION RECOVERY
// =============================================================================
// After running `pnpm exec supabase db reset`, the auth.users table is cleared but
// the browser still has a valid JWT in localStorage. When the app tries to
// create a profile for this non-existent user, it fails with a foreign key
// violation (profiles.id references auth.users.id).
//
// Solution: Detect this specific FK error, sign out the stale session, and
// re-initialize auth with a fresh anonymous user. This avoids the need to
// manually clear localStorage after every database reset.
//
// Related: Supabase getSession() reads from localStorage cache, not server.
// =============================================================================

import { COCKTAIL_ALIASES, USER_COLORS } from './userIdentity'
import { storePreResetIdentity } from './analytics'
import { syncLocaleToUserMetadata } from './userMetadataLocale'
import { routing, type AppLocale } from '@/i18n/routing'
import { isLocalePubliclyEnabled } from '@/i18n/useLocaleSettings'
import { stripLocalePrefix } from '@/i18n/surfaces'
import type { CaptchaTokenProvider } from './authCaptcha'
import { userFacingErrorMessage } from './apiErrors'

// =============================================================================
// AUTH STORAGE KEYS
// =============================================================================
// Centralized constants for localStorage keys used in auth flow.
// This prevents bugs from typos or inconsistent values across files.
// =============================================================================

/** localStorage key for tracking sign-in vs sign-up mode during OAuth */
export const AUTH_SIGN_IN_MODE_KEY = 'authSignInMode'
/** Value stored when user is signing in (not upgrading anonymous account) */
export const AUTH_SIGN_IN_MODE_VALUE = 'signin'
/** localStorage key for persisting the browser locale across OAuth redirects */
export const AUTH_LOCALE_KEY = 'authLocale'

/**
 * Error thrown when a stale session is detected (user no longer exists in DB).
 * This triggers re-authentication with a fresh anonymous session.
 */
class StaleSessionError extends Error {
  constructor() {
    super('Session references a deleted user. Re-authentication required.')
    this.name = 'StaleSessionError'
  }
}

// Old localStorage keys for migration
const OLD_ALIAS_KEY = 'martini-user-alias'
const OLD_COLOR_KEY = 'martini-user-color'

// Retry configuration for transient network errors
const MAX_RETRIES = 3
const INITIAL_RETRY_DELAY_MS = 1000
const COMPLETE_CONVERSION_RETRY_DELAY_MS = 500

/**
 * Check if an error is a transient network error that should be retried.
 * Safari reports "Load failed", other browsers may report "Failed to fetch" or "NetworkError".
 */
function isTransientNetworkError(error: unknown): boolean {
  if (!error) return false
  const message = error instanceof Error ? error.message : String(error)
  return (
    message.includes('Load failed') || // Safari
    message.includes('Failed to fetch') || // Chrome/Firefox
    message.includes('NetworkError') || // Generic
    message.includes('network') ||
    message.includes('ECONNRESET') ||
    message.includes('ETIMEDOUT')
  )
}

/**
 * Sleep for a given number of milliseconds.
 */
function sleep(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms))
}

export interface AuthState {
  userId: string
  isAnonymous: boolean
  profile: {
    alias: string
    color: string
    email: string | null
    locale: AppLocale
    isEditor: boolean
    editorLocales: AppLocale[]
    createdAt: string | null
    convertedAt: string | null
  }
}

export function isSupportedLocale(locale: string | null | undefined): locale is AppLocale {
  return (routing.locales as readonly string[]).includes(locale ?? '')
}

export function normalizeLocale(locale: string | null | undefined): AppLocale {
  return isSupportedLocale(locale) ? locale : routing.defaultLocale
}

function getCurrentBrowserLocale(): AppLocale {
  if (typeof window === 'undefined') return routing.defaultLocale

  const pathLocale = window.location.pathname.split('/')[1]
  if (isSupportedLocale(pathLocale)) return pathLocale

  const cookieLocale = document.cookie
    .split('; ')
    .find(row => row.startsWith('NEXT_LOCALE='))
    ?.split('=')[1]
  if (isSupportedLocale(cookieLocale)) return cookieLocale

  const langPrefix = navigator.language.toLowerCase().split('-')[0]
  const match = routing.locales.find(l => l === langPrefix)
  if (match) return match
  return routing.defaultLocale
}

async function getPublicOAuthLocale(): Promise<AppLocale> {
  const browserLocale = getCurrentBrowserLocale()
  if (browserLocale === routing.defaultLocale) return browserLocale
  return (await isLocalePubliclyEnabled(browserLocale)) ? browserLocale : routing.defaultLocale
}

// Set when GoTrue rejects signInAnonymously with anonymous_provider_disabled —
// covers config drift where Supabase has anonymous auth off but this build is
// missing NEXT_PUBLIC_ANONYMOUS_SIGN_INS_DISABLED. Flipped before initAuth
// resolves, so the SignedOutGate (which renders after auth loading settles)
// sees it on its next evaluation.
let runtimeAnonymousAuthDisabled = false

/**
 * Whether this deployment disables anonymous sign-ins. Enterprise SSO-only
 * instances set NEXT_PUBLIC_ANONYMOUS_SIGN_INS_DISABLED=true alongside the
 * Supabase-side `enable_anonymous_sign_ins = false`, so session-less visitors
 * stay signed out (the app shows a sign-in gate) instead of bootstrapping an
 * anonymous session. Also true when GoTrue rejected an anonymous sign-in at
 * runtime (flag drift). Unset on consumer deployments — behavior unchanged.
 */
export function isAnonymousAuthDisabled(): boolean {
  return process.env.NEXT_PUBLIC_ANONYMOUS_SIGN_INS_DISABLED === 'true' || runtimeAnonymousAuthDisabled
}

/**
 * Whether the signed-out UI should expose enterprise SSO entry points.
 * Consumer production leaves this unset; dedicated enterprise builds can opt in
 * explicitly, and SSO-only builds opt in through the anonymous-auth kill switch.
 */
export function isEnterpriseSsoEnabled(): boolean {
  return process.env.NEXT_PUBLIC_ENTERPRISE_SSO_ENABLED === '1' || isAnonymousAuthDisabled()
}

/** Whether the browser is on /auth/callback (with or without a locale prefix). */
function isOnAuthCallbackPath(): boolean {
  if (typeof window === 'undefined') return false
  return stripLocalePrefix(window.location.pathname).pathname.startsWith('/auth/callback')
}

/**
 * Initialize auth on app load.
 * Creates anonymous session if none exists.
 * Handles migration from localStorage.
 *
 * Returns null (signed out, not an error) instead of creating an anonymous
 * session when anonymous auth is disabled for this deployment — the
 * SignedOutGate then shows the sign-in flow.
 *
 * Also handles stale session recovery: if the cached session references a
 * user that no longer exists (e.g., after db reset), we sign out and retry.
 */
export async function initAuth(getCaptchaToken: CaptchaTokenProvider): Promise<AuthState | null> {
  // Check for existing session
  const {
    data: { session },
  } = await supabase.auth.getSession()

  if (session) {
    // A cached anonymous session (e.g. from before an SSO-only rollout) must
    // not walk past the sign-in gate: treat it as signed out. Clear it too —
    // except on /auth/callback, where the in-flight code exchange owns the
    // session and a concurrent signOut could revoke the freshly established
    // SSO session instead.
    if (isAnonymousAuthDisabled() && (session.user.is_anonymous ?? false)) {
      if (!isOnAuthCallbackPath()) {
        await supabase.auth.signOut({ scope: 'local' }).catch(err => {
          console.warn('[Auth] Failed to sign out stale anonymous session:', err)
        })
      }
      return null
    }

    try {
      // Existing session - fetch profile
      const profile = await getOrCreateProfile(session.user.id)

      // Migrate old localStorage data if present
      await migrateFromLocalStorage(session.user.id)

      return {
        userId: session.user.id,
        isAnonymous: session.user.is_anonymous ?? true,
        profile,
      }
    } catch (err) {
      // Handle stale session: user was deleted from database but JWT is still cached
      if (err instanceof StaleSessionError) {
        console.log('[Auth] Clearing stale session and creating fresh anonymous user...')
        await supabase.auth.signOut({ scope: 'local' })
        // Recursively call initAuth - this time there's no session, so we'll
        // fall through to create a new anonymous user
        return initAuth(getCaptchaToken)
      }
      throw err
    }
  }

  // The callback page owns installing the redirected Supabase session. Starting
  // an anonymous sign-in here races detectSessionInUrl / exchangeCodeForSession,
  // and a slower anonymous response can overwrite the real session in storage.
  // The callback redirects after finishing, and the destination initializes the
  // provider normally with the newly persisted session.
  if (isOnAuthCallbackPath()) {
    return null
  }

  // No session and this deployment doesn't allow anonymous sessions: the
  // visitor stays signed out and the app shows the sign-in gate.
  if (isAnonymousAuthDisabled()) {
    return null
  }

  // No session - create anonymous user (with retry for transient network errors)
  let lastError: Error | null = null

  for (let attempt = 0; attempt < MAX_RETRIES; attempt++) {
    const challenge = await getCaptchaToken().then(
      token => ({ token }),
      error => ({ error }),
    )
    // A challenge may wait for user interaction. A real sign-in or OAuth
    // callback that started meanwhile owns the session; never replace it with
    // the delayed anonymous request.
    if (isOnAuthCallbackPath()) return null
    const { data: current } = await supabase.auth.getSession()
    if (current.session) return initAuth(getCaptchaToken)
    if ('error' in challenge) throw challenge.error
    const { data, error } = await supabase.auth.signInAnonymously({ options: { captchaToken: challenge.token } })

    // Supabase has anonymous sign-ins disabled but the frontend flag wasn't set
    // (config drift). Degrade to the signed-out gate — which is the intended
    // experience on such a deployment — rather than the connection-error screen.
    // The runtime flag arms the SignedOutGate, which otherwise only checks the
    // build-time env var and would render protected children signed-out.
    if (error && (error as { code?: string }).code === 'anonymous_provider_disabled') {
      console.warn(
        '[Auth] Anonymous sign-ins are disabled on this Supabase project but ' +
          'NEXT_PUBLIC_ANONYMOUS_SIGN_INS_DISABLED is not set — treating visitor as signed out.',
      )
      runtimeAnonymousAuthDisabled = true
      return null
    }

    if (!error && data.user) {
      // Success - continue with profile creation below
      const oldAlias = typeof window !== 'undefined' ? localStorage.getItem(OLD_ALIAS_KEY) : null
      const oldColor = typeof window !== 'undefined' ? localStorage.getItem(OLD_COLOR_KEY) : null
      const profile = await createProfile(data.user.id, oldAlias || undefined, oldColor || undefined)

      if (typeof window !== 'undefined') {
        localStorage.removeItem(OLD_ALIAS_KEY)
        localStorage.removeItem(OLD_COLOR_KEY)
      }

      return {
        userId: data.user.id,
        isAnonymous: true,
        profile,
      }
    }

    // Check if this is a retryable network error
    const errorMessage = error?.message || 'Unknown error'
    lastError = new Error(`Failed to create anonymous session: ${errorMessage}`)

    if (isTransientNetworkError(error)) {
      // Only sleep if there are more attempts remaining
      if (attempt < MAX_RETRIES - 1) {
        const delay = INITIAL_RETRY_DELAY_MS * Math.pow(2, attempt)
        console.log(`[Auth] Network error on attempt ${attempt + 1}/${MAX_RETRIES}, retrying in ${delay}ms...`)
        await sleep(delay)
      }
      continue
    }

    // Non-retryable error - throw immediately
    throw lastError
  }

  // All retries exhausted
  throw lastError || new Error('Failed to create anonymous session after retries')
}

/**
 * Fetch the caller's per-locale editor grants. Returns `[]` for non-editors.
 */
async function fetchEditorLocales(userId: string): Promise<AppLocale[]> {
  const { data, error } = await supabase.from('i18n_editor_grants').select('locale').eq('user_id', userId)

  if (error || !data) return []

  const locales: AppLocale[] = []
  for (const row of data) {
    if (isSupportedLocale(row.locale)) locales.push(row.locale)
  }
  return locales
}

type ProfileBundle = {
  alias: string
  color: string
  email: string | null
  locale: AppLocale
  isEditor: boolean
  editorLocales: AppLocale[]
  createdAt: string | null
  convertedAt: string | null
}

/**
 * Get existing profile or create if missing
 */
async function getOrCreateProfile(userId: string): Promise<ProfileBundle> {
  // Profile and editor grants are independent reads; fetch them together.
  const [{ data: profile }, editorLocales] = await Promise.all([
    supabase.from('profiles').select('alias, color, email, locale, created_at, converted_at').eq('id', userId).single(),
    fetchEditorLocales(userId),
  ])

  if (profile) {
    // Presence stamp — nothing downstream reads it, so don't hold sign-in on the write.
    supabase
      .from('profiles')
      .update({ last_active_at: new Date().toISOString() })
      .eq('id', userId)
      .then(
        () => {},
        () => {},
      )

    return {
      alias: profile.alias,
      color: profile.color,
      email: profile.email ?? null,
      locale: normalizeLocale(profile.locale),
      isEditor: editorLocales.length > 0,
      editorLocales,
      createdAt: profile.created_at ?? null,
      convertedAt: profile.converted_at ?? null,
    }
  }

  // Profile missing (shouldn't happen, but handle gracefully)
  return createProfile(userId)
}

/**
 * Resolve the locale to persist on a brand-new profile.
 *
 * An explicit `locale` (e.g. the locale a user deliberately signed up in) is
 * honored as-is. When omitted we fall back to the *implicit* browser signal
 * (`navigator.language` / cookie / path) — but a first-time anonymous user
 * whose browser is a launch-gated locale must NOT have that hidden locale
 * persisted as their profile preference: `resolveAppLocale` and the AuthContext
 * cookie-seed both treat `profiles.locale` as an explicit choice, so persisting
 * an implicit hidden locale here would leak it into the active UI bundle. Gate
 * the implicit value behind the public `i18n_locale_settings` flag and fall
 * back to the default locale for not-yet-launched locales (new anonymous users
 * are never editors, so there is no preview carve-out to preserve).
 */
async function resolveCreationLocale(explicit?: string): Promise<AppLocale> {
  if (explicit !== undefined) return normalizeLocale(explicit)
  const browserLocale = normalizeLocale(getCurrentBrowserLocale())
  if (browserLocale === routing.defaultLocale) return browserLocale
  return (await isLocalePubliclyEnabled(browserLocale)) ? browserLocale : routing.defaultLocale
}

/**
 * Create profile with random cocktail alias and color
 */
async function createProfile(userId: string, alias?: string, color?: string, locale?: string): Promise<ProfileBundle> {
  const finalAlias = alias || COCKTAIL_ALIASES[Math.floor(Math.random() * COCKTAIL_ALIASES.length)]
  const finalColor = color || USER_COLORS[Math.floor(Math.random() * USER_COLORS.length)]
  const finalLocale = await resolveCreationLocale(locale)

  // Use upsert to handle race conditions and existing profiles
  const { data, error } = await supabase
    .from('profiles')
    .upsert(
      { id: userId, alias: finalAlias, color: finalColor, locale: finalLocale },
      { onConflict: 'id', ignoreDuplicates: false },
    )
    .select('alias, color, email, locale, created_at, converted_at')
    .single()

  if (error) {
    // -------------------------------------------------------------------------
    // STALE SESSION DETECTION
    // -------------------------------------------------------------------------
    // PostgreSQL error code 23503 = foreign_key_violation
    // This happens when the user ID from a cached JWT no longer exists in
    // auth.users (e.g., after `pnpm exec supabase db reset`).
    //
    // We detect this specific case and throw StaleSessionError so initAuth
    // can sign out and create a fresh anonymous session.
    // -------------------------------------------------------------------------
    const isForeignKeyViolation = error.code === '23503'
    const isProfilesConstraint = error.message?.includes('profiles_id_fkey')

    if (isForeignKeyViolation && isProfilesConstraint) {
      console.warn(
        '[Auth] Stale session detected: user no longer exists in database.\n' +
          '       This typically happens after `pnpm exec supabase db reset`.\n' +
          '       Signing out and creating fresh anonymous session...',
      )
      throw new StaleSessionError()
    }

    // If upsert failed for other reasons, try to fetch existing profile
    const { data: existing } = await supabase
      .from('profiles')
      .select('alias, color, email, locale, created_at, converted_at')
      .eq('id', userId)
      .single()

    if (existing) {
      const existingLocale = normalizeLocale(existing.locale)
      void syncLocaleToUserMetadata(existingLocale)
      const editorLocales = await fetchEditorLocales(userId)
      return {
        alias: existing.alias,
        color: existing.color,
        email: existing.email ?? null,
        locale: existingLocale,
        isEditor: editorLocales.length > 0,
        editorLocales,
        createdAt: existing.created_at ?? null,
        convertedAt: existing.converted_at ?? null,
      }
    }
    throw new Error(`Failed to create profile: ${error.message}`)
  }

  // Mirror locale into auth.users.raw_user_meta_data for auth-email templates.
  void syncLocaleToUserMetadata(finalLocale)

  if (!data) {
    return {
      alias: finalAlias,
      color: finalColor,
      email: null,
      locale: finalLocale,
      isEditor: false,
      editorLocales: [],
      createdAt: null,
      convertedAt: null,
    }
  }

  const editorLocales = await fetchEditorLocales(userId)
  return {
    alias: data.alias,
    color: data.color,
    email: data.email ?? null,
    locale: normalizeLocale(data.locale),
    isEditor: editorLocales.length > 0,
    editorLocales,
    createdAt: data.created_at ?? null,
    convertedAt: data.converted_at ?? null,
  }
}

/**
 * Migrate from localStorage to Supabase profile
 */
async function migrateFromLocalStorage(userId: string): Promise<void> {
  if (typeof window === 'undefined') return

  const oldAlias = localStorage.getItem(OLD_ALIAS_KEY)
  const oldColor = localStorage.getItem(OLD_COLOR_KEY)

  if (oldAlias || oldColor) {
    // Update profile with old values
    const updates: { alias?: string; color?: string } = {}
    if (oldAlias) updates.alias = oldAlias
    if (oldColor) updates.color = oldColor

    await supabase.from('profiles').update(updates).eq('id', userId)

    // Clear old keys
    localStorage.removeItem(OLD_ALIAS_KEY)
    localStorage.removeItem(OLD_COLOR_KEY)
  }
}

/**
 * Update user alias
 */
export async function updateAlias(newAlias: string): Promise<void> {
  const {
    data: { user },
  } = await supabase.auth.getUser()
  if (!user) return

  await supabase.from('profiles').update({ alias: newAlias.trim() }).eq('id', user.id)
}

/**
 * Update user color
 */
export async function updateColor(newColor: string): Promise<void> {
  const {
    data: { user },
  } = await supabase.auth.getUser()
  if (!user) return

  await supabase.from('profiles').update({ color: newColor }).eq('id', user.id)
}

/**
 * Update user locale preference.
 *
 * Updates profiles.locale (source of truth) and mirrors into
 * auth.users.raw_user_meta_data.locale so Supabase auth-email templates
 * pick up the new locale on the next email.
 */
export async function updateLocalePreference(locale: AppLocale): Promise<void> {
  const {
    data: { user },
  } = await supabase.auth.getUser()
  if (!user) return

  const { error } = await supabase.from('profiles').update({ locale }).eq('id', user.id)
  if (error) {
    throw new Error(`Failed to update locale preference: ${error.message}`)
  }
  void syncLocaleToUserMetadata(locale)
}

/**
 * Get current user's profile
 */
export async function getProfile(): Promise<{
  alias: string
  color: string
  email: string | null
  locale: AppLocale
  isEditor: boolean
  editorLocales: AppLocale[]
} | null> {
  const {
    data: { user },
  } = await supabase.auth.getUser()
  if (!user) return null

  const { data } = await supabase.from('profiles').select('alias, color, email, locale').eq('id', user.id).single()

  if (!data) return null

  const editorLocales = await fetchEditorLocales(user.id)

  return {
    alias: data.alias,
    color: data.color,
    email: data.email ?? null,
    locale: normalizeLocale(data.locale),
    isEditor: editorLocales.length > 0,
    editorLocales,
  }
}

/** Whether the current session is a guest (anonymous) account. Reads the cached session, no network. */
export async function isAnonymousSession(): Promise<boolean> {
  const {
    data: { session },
  } = await supabase.auth.getSession()
  return session?.user.is_anonymous ?? false
}

/**
 * Get current session's access token (for PartyKit auth)
 * Automatically refreshes token if expired.
 */
export async function getAccessToken(): Promise<string | null> {
  const {
    data: { session },
  } = await supabase.auth.getSession()

  if (!session) {
    return null
  }

  // Check if token is expired or about to expire (within 60 seconds)
  const expiresAt = session.expires_at ? session.expires_at * 1000 : 0
  const now = Date.now()
  const shouldRefresh = expiresAt - now < 60000 // Refresh if < 1 minute left

  if (shouldRefresh) {
    console.log('[Auth] Token expiring soon, refreshing...')
    const refreshed = await refreshSession({ forUserId: session.user?.id })
    if (refreshed) {
      return refreshed.access_token
    }

    // A *proactive* refresh (token still valid) that fails doesn't invalidate the
    // token we already hold — supabase-js deliberately keeps such a session alive,
    // and caches the failure for a cooldown window so an immediate retry just
    // replays it. Use the token we have rather than hard-failing a request that
    // would succeed; if the backend does reject it, authenticatedRequest refreshes
    // and retries on the 401. Mirrors ensureFreshAccessToken() below.
    return expiresAt > now ? session.access_token : null
  }

  return session.access_token
}

/**
 * Return an access token with enough lifetime for a long-running operation.
 *
 * Unlike `getAccessToken()`, which refreshes only in the final minute, this lets
 * callers that are about to start an extended flow refresh early without
 * rotating fresh anonymous sessions on every call.
 */
export async function ensureFreshAccessToken(minTtlMs = 5 * 60 * 1000): Promise<string | null> {
  const {
    data: { session },
  } = await supabase.auth.getSession()

  if (!session) return null

  const expiresAt = session.expires_at ? session.expires_at * 1000 : 0
  const shouldRefresh = expiresAt - Date.now() < minTtlMs
  if (!shouldRefresh) return session.access_token

  const refreshed = await refreshSession({ forUserId: session.user?.id })
  return refreshed?.access_token ?? session.access_token
}

/**
 * Refresh the current session token
 * @param options.forUserId the user the caller's session belonged to. When set,
 *   a refresh that lost a race is allowed to fall back to the session another
 *   writer stored — but only if it is still that user's (see
 *   `sessionRefreshedElsewhere`). Without it a lost race is reported as a failure.
 * @returns New session or null if refresh failed
 */
export async function refreshSession(options: { forUserId?: string } = {}): Promise<{
  access_token: string
  expires_at: number
} | null> {
  try {
    const { data, error } = await supabase.auth.refreshSession()

    if (error) {
      // Two tabs (or a tab and its own auto-refresh timer) waking up together
      // both send the refresh. auth-js commits the first result and discards
      // the second as `AuthRefreshDiscardedError` because storage changed under
      // it — but storage now holds the winner's perfectly valid session. Read
      // it back instead of reporting a failure for a session that just
      // refreshed (Sentry MARTINI-VERCEL-NEXT-1BN: GoTrue rotated the token in
      // the same second the app logged TOKEN_REFRESH_FAILED).
      const adopted = options.forUserId ? await sessionRefreshedElsewhere(options.forUserId) : null
      if (adopted) {
        console.log('[Auth] Session was refreshed concurrently; using the stored session')
        return adopted
      }

      console.error('[Auth] Session refresh failed:', error.message)

      if (error.message.includes('refresh_token_not_found')) {
        console.warn('[Auth] Refresh token not found — session may have been revoked or rotated')
        Sentry.captureMessage('Refresh token not found', {
          level: 'warning',
          tags: { source: 'auth', action: 'refreshSession' },
        })
      }

      return null
    }

    if (!data.session) {
      console.error('[Auth] Refresh succeeded but no session returned')
      return null
    }

    console.log('[Auth] Session refreshed successfully')
    return {
      access_token: data.session.access_token,
      expires_at: data.session.expires_at ?? 0,
    }
  } catch (err) {
    console.error('[Auth] Unexpected error during session refresh:', err)
    return null
  }
}

/**
 * The session in storage after a refresh attempt of ours failed, when it is
 * still usable *and still the same user's*. auth-js refuses to commit a refresh
 * whenever another writer replaced the stored session; usually that is a
 * concurrent refresh of the same session, but it is also what a sign-in for a
 * different account looks like ("switch accounts", accepting an invite as
 * someone else). A request started under user A must never go out carrying
 * user B's token, so a session for anyone but `forUserId` is not adopted.
 */
async function sessionRefreshedElsewhere(
  forUserId: string,
): Promise<{ access_token: string; expires_at: number } | null> {
  const {
    data: { session },
  } = await supabase.auth.getSession()
  if (!session?.access_token) return null
  if (session.user?.id !== forUserId) return null
  const expiresAt = session.expires_at ? session.expires_at * 1000 : 0
  if (expiresAt <= Date.now()) return null
  return { access_token: session.access_token, expires_at: session.expires_at ?? 0 }
}

export type SignOutScope = 'local' | 'global'

/**
 * Sign out. `local` (default) ends this browser's session only. `global` is
 * GoTrue's `DELETE FROM auth.sessions WHERE user_id`, which also revokes the
 * sessions issued to MCP clients (ChatGPT, Claude, Codex, …) and the desktop
 * app — reserve it for a deliberate "sign out everywhere".
 */
export async function signOut(scope: SignOutScope = 'local'): Promise<void> {
  await supabase.auth.signOut({ scope })
}

// ============================================================================
// Account Upgrade Functions
// ============================================================================

export type UpgradeError = 'email_exists' | 'weak_password' | 'invalid_email' | 'unknown'

export interface UpgradeResult {
  success: boolean
  error?: UpgradeError
  message?: string
}

function isEmailAlreadyExistsError(error: unknown): boolean {
  if (error && typeof error === 'object' && (error as { code?: unknown }).code === 'EMAIL_ALREADY_EXISTS') {
    return true
  }

  const message = error instanceof Error ? error.message : String(error)
  return message.includes('Email already in use') || message.includes('EMAIL_ALREADY_EXISTS')
}

async function finishEmailAccountConversion(input: {
  completeConversion: (data: {
    email: string
    name?: string
    referralCode?: string
    referralSource?: ReferralSource
    referralLandingPath?: string
    promoSlug?: string
    method?: 'google' | 'email' | 'unknown'
    visitorId?: string | null
    userLocale?: AppLocale
  }) => Promise<{ organizationId: string; referralRedeemed: boolean }>
  getVisitorId: () => Promise<string | null>
  email: string
  userId: string
  locale: string
}): Promise<UpgradeResult> {
  const referralCode = getStoredReferralCode()
  // Carry where the ?ref= landed (e.g. /m/ → 'motion_share') so signup_completed is
  // attributed correctly — the Google path already does this; email must match.
  const referralContext = getStoredReferralContext()
  const redeemableReferralCode = referralCode && !isSentinelRefCode(referralCode) ? referralCode : undefined
  const promoSlug = getStoredPromoSlug()
  const visitorId = await input.getVisitorId()

  const runConversion = async () =>
    input.completeConversion({
      email: input.email,
      name: input.email.split('@')[0],
      referralCode: redeemableReferralCode,
      referralSource: referralContext?.source,
      referralLandingPath: referralContext?.landingPath || undefined,
      promoSlug: promoSlug || undefined,
      method: 'email',
      visitorId,
      userLocale: normalizeLocale(input.locale),
    })

  try {
    let result: Awaited<ReturnType<typeof runConversion>>
    try {
      result = await runConversion()
    } catch (error) {
      if (isEmailAlreadyExistsError(error)) {
        return { success: false, error: 'email_exists', message: 'This email is already registered' }
      }

      // Supabase can rotate the session when updateUser flips the account out of
      // anonymous mode. Refresh once before treating setup as failed.
      await refreshSession()
      await sleep(COMPLETE_CONVERSION_RETRY_DELAY_MS)
      result = await runConversion()
    }

    console.log('[Auth] Email conversion completed:', {
      organizationId: result.organizationId,
      referralRedeemed: result.referralRedeemed,
    })

    if (referralCode) clearStoredReferralCode()
    if (promoSlug) clearStoredPromoSlug()

    return { success: true }
  } catch (error) {
    console.error('[Auth] Failed to complete conversion:', error)

    if (isEmailAlreadyExistsError(error)) {
      return { success: false, error: 'email_exists', message: 'This email is already registered' }
    }

    Sentry.captureException(error, {
      tags: { source: 'auth', action: 'completeConversion' },
      extra: { email: input.email, userId: input.userId },
    })

    return {
      success: false,
      error: 'unknown',
      message: 'Your account was created, but setup did not finish. Please try again.',
    }
  }
}

/**
 * Upgrade anonymous user to email/password account using OTP verification.
 * This is the new flow that requires email verification before account upgrade.
 *
 * Flow:
 * 1. User enters email → sends OTP via sendOTP()
 * 2. User enters OTP → verifies via verifyOTPCode() → gets verificationToken
 * 3. User enters password → calls this function with verificationToken
 * 4. This function validates the token, then upgrades the account
 */
export async function upgradeWithVerifiedEmail(
  email: string,
  password: string,
  verificationToken: string,
  getCaptchaToken: CaptchaTokenProvider,
  locale: string = getCurrentBrowserLocale(),
): Promise<UpgradeResult> {
  // Import API functions here to avoid circular dependency
  const { completeEmailSignup, completeConversion } = await import('./api')
  const { getVisitorId } = await import('./fingerprint')

  try {
    const {
      data: { user },
    } = await supabase.auth.getUser()

    if (!user) {
      return { success: false, error: 'unknown', message: 'Not authenticated' }
    }

    if (!user.is_anonymous) {
      if (user.email?.toLowerCase() === email.toLowerCase()) {
        return finishEmailAccountConversion({
          completeConversion,
          getVisitorId,
          email,
          userId: user.id,
          locale,
        })
      }
      return { success: false, error: 'unknown', message: 'Already a real account' }
    }

    // Validate the verification token and apply credentials via backend.
    const validationResult = await completeEmailSignup(email, verificationToken, password)
    if (!validationResult.success) {
      return {
        success: false,
        error: 'unknown',
        message: validationResult.message || 'Invalid verification token',
      }
    }

    // Applying the new email+password via admin updateUser revokes the prior
    // (anonymous) session, so refreshSession can come back EMPTY ("Auth session
    // missing!") rather than rotated — there's no session left to refresh. When
    // that happens, re-authenticate with the just-set credentials so the
    // conversion call below (an authenticated request) has a valid session.
    const { error: refreshError } = await supabase.auth.refreshSession()
    if (refreshError) {
      console.warn('[Auth] Session lost after signup completion; re-authenticating with new credentials')
      const captchaToken = await getCaptchaToken()
      const { error: signInError } = await supabase.auth.signInWithPassword({
        email,
        password,
        options: { captchaToken },
      })
      if (signInError) {
        console.error('[Auth] Re-authentication after signup failed:', signInError.message)
        return {
          success: false,
          error: 'unknown',
          message: 'Your account was created — please sign in with your new password.',
        }
      }
    }

    // Complete conversion atomically on the backend. Signup is not considered
    // complete until Martini profile/org setup succeeds.
    const conversionResult = await finishEmailAccountConversion({
      completeConversion,
      getVisitorId,
      email,
      userId: user.id,
      locale,
    })
    if (!conversionResult.success) {
      return conversionResult
    }

    // Send signup notification to admins (fire-and-forget)
    sendSignupNotification(email).catch(err => {
      console.warn('[Auth] Failed to send signup notification:', err)
    })

    return { success: true }
  } catch (err) {
    console.error('[Auth] upgradeWithVerifiedEmail error:', err)
    return {
      success: false,
      error: 'unknown',
      message: userFacingErrorMessage(err, 'Signup could not finish. Please try signing in with your password.'),
    }
  }
}

/**
 * Start the Google OAuth flow, in the system browser when running inside
 * the desktop shell.
 *
 * The embedded Chromium in the desktop app can't complete Google's passkey /
 * "use your phone" 2-step verification (no Bluetooth access, no WebAuthn
 * plumbing), which strands users on Google's 2FA screen. So in the shell we
 * skip the in-window redirect and open the OAuth URL via window.open — the
 * shell's window-open handler routes non-martini.film URLs to the system
 * browser. Supabase then redirects to /auth/desktop-callback, which hands the
 * session back to the app through its martini:// deep link.
 *
 * In a regular browser this is the plain in-window redirect to /auth/callback.
 */
export async function startGoogleOAuth(mode: 'link' | 'sign-in'): Promise<void> {
  const desktop = isDesktopApp()
  const options = {
    redirectTo:
      typeof window !== 'undefined'
        ? `${window.location.origin}${desktop ? '/auth/desktop-callback' : '/auth/callback'}`
        : undefined,
    skipBrowserRedirect: desktop,
  }

  const { data, error } =
    mode === 'link'
      ? await supabase.auth.linkIdentity({ provider: 'google', options })
      : await supabase.auth.signInWithOAuth({ provider: 'google', options })

  if (error) throw error

  if (desktop) {
    if (!data?.url) throw new Error('[startGoogleOAuth] No OAuth URL returned from Supabase')
    window.open(data.url, '_blank')
  }
}

/**
 * Upgrade anonymous user via Google OAuth.
 * This initiates an OAuth flow - user will be redirected.
 *
 * Store return path before calling this function.
 */
export async function upgradeToGoogleAccount(returnPath?: string): Promise<void> {
  // Store return path for after OAuth callback
  if (typeof window !== 'undefined' && returnPath) {
    localStorage.setItem('authReturnPath', returnPath)
  }
  if (typeof window !== 'undefined') {
    localStorage.setItem(AUTH_LOCALE_KEY, await getPublicOAuthLocale())
    // This is a conversion attempt — clear any sign-in marker left by an
    // abandoned earlier log-in so the callback doesn't misclassify a new
    // signup as a sign-in and skip the signup notification.
    localStorage.removeItem(AUTH_SIGN_IN_MODE_KEY)
  }

  // Store PostHog identity before redirect so anonymous activity can be linked
  // to the authenticated profile after the OAuth callback completes
  storePreResetIdentity()

  // Initiate Google OAuth with identity linking
  // This links the Google identity to the existing anonymous account
  try {
    await startGoogleOAuth('link')
  } catch (error) {
    console.error('[Auth] upgradeToGoogleAccount error:', error)
    throw error
  }
}

/**
 * Sign in to existing account (different from upgrade).
 * This is for when an anonymous user realizes they have an existing account.
 *
 * WARNING: This changes the UID. Anonymous projects are not transferred.
 */
export async function signInToExistingAccount(
  email: string,
  password: string,
  captchaToken: string | undefined,
): Promise<{ success: boolean; error?: string }> {
  // Sign in to the existing account
  const { error } = await supabase.auth.signInWithPassword({
    email,
    password,
    options: { captchaToken },
  })

  if (error) {
    return { success: false, error: error.message }
  }

  return { success: true }
}

/**
 * Sign in to existing account via Google OAuth (different from upgrade).
 * This is for when an anonymous user realizes they have an existing account.
 *
 * WARNING: This changes the UID. Projects need to be transferred separately.
 * Store return path before calling this function.
 */
export async function signInToExistingGoogleAccount(returnPath?: string): Promise<void> {
  // Store return path and sign-in mode for after OAuth callback
  if (typeof window !== 'undefined') {
    if (returnPath) {
      localStorage.setItem('authReturnPath', returnPath)
    }
    localStorage.setItem(AUTH_SIGN_IN_MODE_KEY, AUTH_SIGN_IN_MODE_VALUE)
    localStorage.setItem(AUTH_LOCALE_KEY, await getPublicOAuthLocale())
  }

  // Store PostHog identity before redirect so anonymous activity can be linked
  storePreResetIdentity()

  // Sign in to existing account via Google OAuth (not linking identity)
  try {
    await startGoogleOAuth('sign-in')
  } catch (error) {
    console.error('[Auth] signInToExistingGoogleAccount error:', error)
    throw error
  }
}

/**
 * Start an enterprise SSO (SAML) login.
 *
 * Like signInToExistingGoogleAccount, this signs in to a separate account — SSO
 * never links to the current anonymous identity, so the UID changes and any
 * anonymous work is not carried over.
 *
 * Drive this with a providerId resolved from the backend HRD endpoint (resolveSso)
 * so we never hit Supabase's "no provider for this domain" error path. Unlike
 * OAuth, signInWithSSO does NOT auto-redirect — it returns the IdP URL to follow.
 */
export async function signInWithSSO(
  opts: { providerId?: string; domain?: string },
  returnPath?: string,
): Promise<void> {
  if (typeof window !== 'undefined') {
    if (returnPath) {
      localStorage.setItem('authReturnPath', returnPath)
    }
    localStorage.setItem(AUTH_SIGN_IN_MODE_KEY, AUTH_SIGN_IN_MODE_VALUE)
    localStorage.setItem(AUTH_LOCALE_KEY, await getPublicOAuthLocale())
  }

  // Store PostHog identity before redirect so anonymous activity can be linked.
  storePreResetIdentity()

  const redirectTo = typeof window !== 'undefined' ? `${window.location.origin}/auth/callback` : undefined

  const { data, error } = await supabase.auth.signInWithSSO(
    opts.providerId
      ? { providerId: opts.providerId, options: { redirectTo } }
      : { domain: opts.domain ?? '', options: { redirectTo } },
  )

  if (error) {
    console.error('[Auth] signInWithSSO error:', error)
    throw error
  }

  if (!data?.url) {
    // SSO must return an IdP redirect URL. Without one there's nowhere to send the
    // user and the caller's "Redirecting…" state would hang — surface it as an error.
    throw new Error('signInWithSSO returned no redirect URL')
  }

  if (typeof window !== 'undefined') {
    window.location.assign(data.url)
  }
}

/**
 * Request a password reset email via Supabase.
 * Sends an email with a reset link that redirects to /account?reset=1.
 */
export async function requestPasswordReset(
  email: string,
  captchaToken: string | undefined,
): Promise<{ success: boolean; error?: string }> {
  const redirectTo = typeof window !== 'undefined' ? `${window.location.origin}/account?reset=1` : undefined

  const { error } = await supabase.auth.resetPasswordForEmail(email, { redirectTo, captchaToken })

  if (error) {
    console.error('[Auth] requestPasswordReset error:', error)
    return { success: false, error: error.message }
  }

  return { success: true }
}
